import * as React from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import * as ReactDOM from "react-dom/client";
import { Toaster } from "sonner";
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
  redirect,
} from "react-router";
import { RouterProvider } from "react-router/dom";
import "./index.css";
import Layout from "./components/layouts/Layout";
import AuthPage from "./pages/AuthPage";
import AuthProvider from "./components/providers/AuthProvider";
import AbonnementPage from "./pages/AbonnementPage";
import SyndicatPage from "./pages/SyndicatPage";
import AidePage from "./pages/AidePage";
import CoordonneesPage from "./pages/CoordonneesPage";
import BoutiquePage from "./pages/BoutiquePage";
import AttestationsPage from "./pages/AttestationsPage";
import FacturesPage from "./pages/FacturesPage";
import SabonnerPage from "./pages/SabonnerPage";
import SuccessPage from "./pages/SuccessPage";
import ConfirmerAchatPage from "./pages/ConfirmerAchatPage";
import SuccessProduitPage from "./pages/SuccessProduitPage";
import SuccessAugmentationCheptelPage from "./pages/SuccesAugmentationCheptel";
import { findUser } from "./lib/services/users.service";
import { findCivilites } from "./lib/services/referentiels.service";
import { findRevueNumerique } from "./lib/services/revues-numeriques.service";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import RevueViewerPage from "./pages/RevueViewerPage";
import { pdfjs } from "react-pdf";
import RevuesNumeriquesPage from "./pages/RevuesNumeriquesPage";
import ChoixTypeAbonnementPage from "./pages/ChoixTypeAbonnementPage";
import WebinairesPage from "./pages/WebinairesPage";
import PetitesAnnoncesPage from "./pages/PetitesAnnoncesPage";
import * as Sentry from "@sentry/react";
import UnsupporterBrowserPage from "./pages/UnsupporterBrowserPage";
import PublierPetiteAnnoncePage from "./pages/PublierPetiteAnnoncePage";
import { petitesAnnoncesService } from "./lib/services/petites-annonces.service";
import { PetiteAnnonce } from "./lib/interfaces/petite-annonce.interface";
import SuccessPaiementPetiteAnnoncePage from "./pages/SuccesPaiementPetiteAnnoncePage";
import PreferencesCommunicationPage from "./pages/PreferencesCommunicationPage";
import { verifyResetPasswordToken } from "./lib/services/auth.service";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 10 * 1000 },
  },
});

Sentry.init({
  dsn: "https://f293bb4dc9c595c2fdbcd6f2e9b5a66f@o4507905988886528.ingest.de.sentry.io/4508812559450192",
  integrations: [
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  tracesSampleRate: 1.0,
  enabled: import.meta.env.PROD,
});

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

const router = sentryCreateBrowserRouter([
  {
    path: "*",
    element: <div>Cette page n'existe pas !</div>,
  },
  {
    path: "/login",
    element: <AuthPage />,
  },
  {
    path: "/auth/reset-password",
    element: <ResetPasswordPage />,
    loader: async ({ request }: { request: Request }) => {
      const url = new URL(request.url);
      const token = url.searchParams.get("token");
      if (!token) {
        return { token: null, isTokenValid: false };
      }
      const isTokenValid = await verifyResetPasswordToken({ tokenId: token });
      return {
        token,
        isTokenValid,
      };
    },
  },
  {
    element: <Layout />,
    children: [
      {
        path: "/",
        element: <AbonnementPage />,
      },
      {
        path: "/syndicat",
        element: <SyndicatPage />,
      },
      {
        path: "/aide",
        element: <AidePage />,
      },
      {
        path: "/coordonnees",
        element: <CoordonneesPage />,
        loader: async () => {
          const civPromise = findCivilites();
          const userPromise = findUser();
          const [civilites, user] = await Promise.all([civPromise, userPromise]);
          return { civilites, user };
        },
      },
      {
        path: "/boutique",
        element: <BoutiquePage />,
      },
      {
        path: "/attestations",
        element: <AttestationsPage />,
      },
      {
        path: "/factures",
        element: <FacturesPage />,
      },
      {
        path: "/sabonner",
        element: <ChoixTypeAbonnementPage />,
      },
      {
        path: "/sabonner/:type",
        element: <SabonnerPage />,
        errorElement: <div>Cette page n'existe pas !</div>,
        loader: async ({ params }: { params: { type: string } }) => {
          const { type } = params;
          if (type !== "papier" && type !== "numerique") {
            throw new Error("Type d'abonnement invalide");
          }
          return { type };
        },
      },
      {
        path: "/paiement/succes",
        element: <SuccessPage />,
      },
      {
        path: "/paiement/succes-cheptel",
        element: <SuccessAugmentationCheptelPage />,
      },
      {
        path: "/paiement/succes-produit",
        element: <SuccessProduitPage />,
      },
      {
        path: "/boutique/confirmation-achat",
        element: <ConfirmerAchatPage />,
      },
      {
        path: "/revues-numeriques",
        element: <RevuesNumeriquesPage />,
      },
      {
        path: "/webinaires",
        element: <WebinairesPage />,
      },
      {
        path: "/preferences-communication",
        element: <PreferencesCommunicationPage />,
        loader: async () => {
          const user = await findUser();
          return { user };
        },
      },
      {
        path: "/petites-annonces",
        element: <PetitesAnnoncesPage />,
      },
      {
        path: "/petites-annonces/succes-paiement",
        element: <SuccessPaiementPetiteAnnoncePage />,
      },
      {
        path: "/petites-annonces/publier",
        element: <PublierPetiteAnnoncePage />,
        loader: async ({ request }: { request: Request }) => {
          try {
            const url = new URL(request.url);
            const id = url.searchParams.get("duplicateId");
            let annonceADupliquer: PetiteAnnonce | null = null;
            if (id) {
              annonceADupliquer = await petitesAnnoncesService.findById(id);
            }
            const creationData = await petitesAnnoncesService.getCreationData();
            return { annonceADupliquer, ...creationData };
          } catch (error) {
            return redirect("/petites-annonces");
          }
        },
      },
      // {
      //   path: "/petites-annonces/:id/modifier",
      //   element: <PublierPetiteAnnoncePage />,
      //   loader: async ({ params }: { params: { id: string } }) => {
      //     try {
      //       const { id } = params;
      //       const [annonce, creationData] = await Promise.all([
      //         findPetiteAnnonceById(id),
      //         getCreatePetiteAnnonceData(),
      //       ]);
      //       return { annonceAModifier: annonce, ...creationData };
      //     } catch (error) {
      //       return redirect("/petites-annonces");
      //     }
      //   },
      // },
      {
        path: "/navigateur-incompatible",
        element: <UnsupporterBrowserPage />,
      },
      {
        path: "/revue/:id",
        element: <RevueViewerPage />,
        errorElement: <div>Revue inaccessible.</div>,
        loader: async ({ params }: { params: { id: string } }) => {
          const { id } = params;
          try {
            const revue = await findRevueNumerique(id);
            const password = import.meta.env.VITE_PDF_PASSWORD;
            if (!revue) {
              console.error("Revue non trouvée");
              return redirect("/revues-numeriques");
            }
            return { revue, password };
          } catch (error) {
            console.error("Erreur lors de la récupération de la revue", error);
            return redirect("/revues-numeriques");
          }
        },
      },
    ],
  },
]);

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Toaster richColors />
        <RouterProvider router={router} />
      </AuthProvider>
    </QueryClientProvider>
  </React.StrictMode>
);
